import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { apiGet, apiPost } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { useToast } from "../../shared/ToastContext";
import { DetailSectionsPreview } from "./DetailSectionsPreview";

function resolveMediaUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  const base = getApiBaseUrl();
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function getValue(row, key) {
  if (!row || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  return row[key] ?? row[pascalKey];
}

export function ViewProductPage() {
  const { id: idParam } = useParams();
  const id = idParam && /^\d+$/.test(String(idParam)) ? Number(idParam) : NaN;
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cloning, setCloning] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!Number.isFinite(id)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet(`/api/Products/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        toast.push(String(res.error || "Failed to load product"), "error");
        return;
      }
      setData(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, toast]);

  const onClone = async () => {
    if (!Number.isFinite(id)) return;
    const confirmed = window.confirmDialog
      ? await window.confirmDialog("Clone this product? A new copy will be created.")
      : window.confirm("Clone this product? A new copy will be created.");
    if (!confirmed) return;

    setCloning(true);
    const res = await apiPost(`/api/Products/${id}/clone`, {});
    setCloning(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to clone product"), "error");
      return;
    }
    const newId = Number(res.data?.id ?? res.data?.Id ?? 0);
    toast.push("Product cloned", "success");
    if (newId) navigate(`/products/${newId}/edit`);
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
        Loading...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
        Product not found.
      </div>
    );
  }

  const mode = getValue(data, "detailContentMode") === "json" ? "json" : "html";
  const image = resolveMediaUrl(getValue(data, "image"));
  const gallery = Array.isArray(data.images)
    ? data.images
    : Array.isArray(data.Images)
      ? data.Images
      : [];
  const contentHtml = DOMPurify.sanitize(String(getValue(data, "content") || ""));
  const sectionsJson = getValue(data, "sectionsJson") || "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">{getValue(data, "name")}</h1>
            <span
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                mode === "json"
                  ? "bg-violet-50 text-violet-700 ring-1 ring-violet-200"
                  : "bg-sky-50 text-sky-700 ring-1 ring-sky-200"
              }`}
            >
              {mode === "json" ? "JSON sections" : "HTML content"}
            </span>
          </div>
          {getValue(data, "subtitle") ? (
            <p className="mt-1 text-sm text-slate-600">{getValue(data, "subtitle")}</p>
          ) : null}
          <p className="mt-1 text-xs text-slate-500">
            Preview uses the selected detail method — websites should do the same via{" "}
            <code className="rounded bg-slate-100 px-1">detailContentMode</code>.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void onClone()}
            disabled={cloning}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {cloning ? "Cloning..." : "Clone"}
          </button>
          <Link
            to={`/products/${id}/edit`}
            className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Edit
          </Link>
          <Link
            to="/products"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back
          </Link>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            {image ? (
              <img src={image} alt="" className="aspect-square w-full rounded-xl object-cover" />
            ) : (
              <div className="flex aspect-square items-center justify-center rounded-xl bg-slate-100 text-sm text-slate-400">
                No image
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow-sm">
            <dl className="space-y-2">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Code</dt>
                <dd className="font-medium text-slate-900">{getValue(data, "code") || "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Barcode</dt>
                <dd className="font-medium text-slate-900">{getValue(data, "barcode") || "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Category</dt>
                <dd className="font-medium text-slate-900">{getValue(data, "categoryName") || "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Brand</dt>
                <dd className="font-medium text-slate-900">{getValue(data, "brandName") || "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Unit</dt>
                <dd className="font-medium text-slate-900">{getValue(data, "unitName") || "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Min qty</dt>
                <dd className="font-medium text-slate-900">{getValue(data, "minQuantity") ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Price</dt>
                <dd className="font-medium text-slate-900">
                  {getValue(data, "discountPrice") != null ? (
                    <>
                      {getValue(data, "discountPrice")}{" "}
                      <span className="text-xs text-slate-400 line-through">{getValue(data, "price")}</span>
                    </>
                  ) : (
                    getValue(data, "price") ?? "—"
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="space-y-4">
          {getValue(data, "shortDescription") ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm leading-6 text-slate-600 shadow-sm">
              {getValue(data, "shortDescription")}
            </div>
          ) : null}

          {getValue(data, "description") ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm leading-6 text-slate-600 shadow-sm">
              <h2 className="mb-2 text-sm font-semibold text-slate-900">Catalog description</h2>
              {getValue(data, "description")}
            </div>
          ) : null}

          {gallery.length > 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-sm font-semibold text-slate-900">Gallery</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {gallery.map((item, index) => {
                  const src = resolveMediaUrl(item.image ?? item.Image);
                  if (!src) return null;
                  return (
                    <img
                      key={item.id ?? item.Id ?? index}
                      src={src}
                      alt=""
                      className="aspect-square rounded-xl border border-slate-200 object-cover"
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
            <div className="mb-3 flex items-center justify-between gap-2 px-1">
              <h2 className="text-sm font-semibold text-slate-900">Detail page preview</h2>
              <span className="text-xs text-slate-500">
                Mode: {mode === "json" ? "JSON sections" : "HTML / page builder"}
              </span>
            </div>

            {mode === "json" ? (
              <DetailSectionsPreview sectionsJson={sectionsJson} product={data} />
            ) : contentHtml.trim() ? (
              <div
                className="blog-content overflow-hidden rounded-2xl border border-slate-200 bg-white p-6"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
                No HTML landing content yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
